import type { GridColDef, GridRenderCellParams, GridRenderEditCellParams } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import type { LexicalSlashMacroAction } from '../../lexical/LexicalSlashMacroPlugin';

export type RichTextSlashMacroActions =
    | ReadonlyArray<LexicalSlashMacroAction>
    | ((params: GridRenderEditCellParams) => ReadonlyArray<LexicalSlashMacroAction>);
export type RichTextDisabledTooltip =
    | ReactNode
    | ((params: GridRenderCellParams) => ReactNode | undefined);

/**
 * Columns flagged with `richText: true` get a rich read-only renderer
 * (sanitized HTML with line clamping) and a Lexical-based inline editor
 * when the grid is rendered with `enableRichTextEditing`. When that prop
 * is off (the default), flagged columns keep their standard renderers.
 */
export type RichTextGridColDef = GridColDef & {
    /** Marks the column as a Rich Text field (e.g. fields of type `note`). */
    richText?: boolean;
    /** Number of lines to clamp the read-only rich content to. Defaults to 1. */
    richTextClampLines?: number;
    /** Optional slash macro actions available while editing this rich text cell. Can be row-aware via edit params. */
    richTextSlashMacroActions?: RichTextSlashMacroActions;
    /** Opens the slash macro automatically when this rich text cell editor is activated. */
    richTextAutoOpenSlashMacro?: boolean;
    /** Allows root clicks to retry auto-open when auto-open is enabled and not suppressed or already opened. */
    richTextEnableSlashMacroAutoOpenOnRootClick?: boolean;
    /** Temporarily hides the auto-open slash macro while another surface owns the macro flow. */
    richTextSuppressAutoOpenSlashMacro?: boolean;
    /** Optional tooltip shown in read mode when this rich text field is disabled for the current row. */
    richTextDisabledTooltip?: RichTextDisabledTooltip;
};

export const isRichTextColumn = (column: GridColDef): column is RichTextGridColDef =>
    (column as RichTextGridColDef).richText === true;
